import { User } from '../types';
import { api } from './ApiClient';

// 게스트 토큰 관련 API
export const getGuestToken = async (): Promise<string> => {
  try {
    const response = await api.post<{ success: boolean; message: string; result: string }>('/api/user/GetGuestToken');
    return response.result || '';
  } catch (error) {
    // 게스트 토큰 요청 실패 - 인증 초기화 문제 진단을 위해 로그 유지
    console.error('게스트 토큰 요청 실패:', error);
    throw error;
  }
};

// 로그인 API
export const getTokenByLoginId = async (guestToken: string, loginId: string, password: string, deviceType: string, deviceId: string): Promise<string> => {
  try {
    const response = await api.post<{ success: boolean; message: string; result: string }>(
      '/api/user/GetTokenByLoginId',
      { login_id: loginId, password, device_type: deviceType, device_id: deviceId },
      guestToken
    );
    
    if (!response.success) {
      throw new Error(response.message || '로그인에 실패했습니다.');
    }
    
    return response.result;
  } catch (error) {
    console.error('로그인 실패:', error);
    throw error;
  }
};

// 회원가입 API
export const signUp = async (guestToken: string, data: {
  login_name: string;
  password: string;
  check_number: string;
  login_id: string;
  mail_address: string;
  device_type: string;
  device_id: string;
}): Promise<User> => {
  try {
    const response = await api.post<{ success: boolean; message: string; result: User }>(
      '/api/user/AddMemberByLoginId',
      data,
      guestToken
    );
    
    if (!response.success) {
      throw new Error(response.message || '회원가입에 실패했습니다.');
    }
    
    return response.result;
  } catch (error) {
    // 회원가입 실패 - 사용자 등록 문제 진단을 위해 로그 유지
    console.error('회원가입 실패:', error);
    throw error;
  }
};

// 이메일 인증 코드 전송 API
export const sendVerificationCode = async (guestToken: string, email: string): Promise<void> => {
  try {
    const response = await api.post<{ success: boolean; message: string }>(
      `/api/user/SendMailToCheckMailAddress?mail_address=${email}`,
      null,
      guestToken
    );
    
    if (!response.success) {
      throw new Error(response.message || '인증 이메일 전송에 실패했습니다.');
    }
  } catch (error) {
    // 인증 이메일 전송 실패 - 이메일 전송 문제 진단을 위해 로그 유지
    console.error('인증 이메일 전송 실패:', error);
    throw error;
  }
};

// 이메일 인증 코드 확인 API
export const checkVerificationCode = async (guestToken: string, email: string, code: string): Promise<boolean> => {
  try {
    const response = await api.post<{ success: boolean; message: string }>(
      `/api/user/CheckMailAddress?mail_address=${email}&check_number=${code}`,
      null,
      guestToken
    );

    if (!response.success) {
      throw new Error(response.message || '이메일 확인 코드 검증에 실패했습니다.');
    }

    return response.success;
  } catch (error) {
    // 이메일 인증 실패 - 인증 코드 검증 문제 진단을 위해 로그 유지
    console.error('이메일 인증 실패:', error);
    throw error;
  }
};

// 비밀번호 복구 이메일 발송 API (로그인 전)
export const sendRecoveryEmail = async (guestToken: string, email: string): Promise<void> => {
  const response = await api.post<{ success: boolean; message: string }>(
    `/api/user/SendMailToRecoveryId?mail_address=${encodeURIComponent(email)}`,
    null,
    guestToken
  );
  if (!response.success) {
    throw new Error(response.message || '이메일 전송에 실패했습니다.');
  }
};

// 비밀번호 재설정 API (인증 코드 + 새 비밀번호)
export const resetPasswordWithCode = async (guestToken: string, email: string, code: string, newPassword: string): Promise<void> => {
  const response = await api.post<{ success: boolean; message: string }>(
    `/api/user/ResetPasswordWithCode?mail_address=${encodeURIComponent(email)}&code=${encodeURIComponent(code)}&new_password=${encodeURIComponent(newPassword)}`,
    null,
    guestToken
  );
  if (!response.success) {
    throw new Error(response.message || '비밀번호 재설정에 실패했습니다.');
  }
};

// 로그아웃 API
export const logout = async (userToken: string): Promise<void> => {
  // 서버 호출 없이 클라이언트 측에서만 로그아웃 처리
  // 토큰 제거는 AuthContext에서 처리
};

// 사용자 정보 업데이트 API
export const updateProfile = async (
  userToken: string,
  data: {
    name?: string;
    email?: string;
    password?: string;
  }
): Promise<User> => {
  try {
    const response = await api.post<{ success: boolean; message: string; result: User }>(
      '/api/user/UpdateProfile',
      data,
      userToken
    );
    
    if (!response.success) {
      throw new Error(response.message || '프로필 업데이트에 실패했습니다.');
    }
    
    return response.result;
  } catch (error) {
    // 프로필 업데이트 실패 - 사용자 정보 변경 문제 진단을 위해 로그 유지
    console.error('프로필 업데이트 실패:', error);
    throw error;
  }
};

// 인증 서비스 객체
export const authService = {
  getGuestToken,
  getTokenByLoginId,
  signUp,
  sendVerificationCode,
  checkVerificationCode,
  updateProfile
};
